import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Visibility as ViewIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
  Payment as PaymentIcon
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import { useSnackbar } from '../contexts/SnackbarContext';
import { useAuth } from '../contexts/AuthContext';
import { filterSubmissionData, getFieldVisibilityInfo } from '../utils/fieldVisibility';
import PhoneInput from '../components/PhoneInput';
import axios from 'axios';

interface Submission {
  id: string;
  uniqueId?: string;
  editCode?: string;
  formId: string;
  data: Record<string, any>;
  status: 'pending' | 'completed' | 'failed';
  paymentInfo?: {
    status: 'none' | 'pending' | 'completed' | 'failed';
    amount?: number;
  };
  createdAt: string;
  form: {
    title: string;
    fields?: Array<{
      id: string;
      label: string;
      type: string;
    }>;
  };
}

interface Form {
  id: string;
  title: string;
}

const SubmissionsList: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSnackbar } = useSnackbar();
  const { user } = useAuth();

  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [forms, setForms] = useState<Form[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [filters, setFilters] = useState({
    formId: searchParams.get('formId') || '',
    paymentStatus: searchParams.get('paymentStatus') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || ''
  });

  const [viewDialog, setViewDialog] = useState<{
    open: boolean;
    submission: Submission | null;
    loading: boolean;
  }>({
    open: false,
    submission: null,
    loading: false
  });

  const [editDialog, setEditDialog] = useState<{
    open: boolean;
    submission: Submission | null;
    formData: Record<string, any>;
    loading: boolean;
    saving: boolean;
  }>({
    open: false,
    submission: null,
    formData: {},
    loading: false,
    saving: false
  });

  const openSubmissionDialog = async (submission: Submission) => {
    setViewDialog({ open: true, submission, loading: true });
    
    try {
      // Fetch full submission details including form fields
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/${submission.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Full submission details:', response.data);
      setViewDialog({ open: true, submission: response.data, loading: false });
    } catch (error) {
      console.error('Error fetching submission details:', error);
      // Fallback to the original submission data
      setViewDialog({ open: true, submission, loading: false });
      showSnackbar('Could not load full submission details', 'warning');
    }
  };

  const fetchSubmissions = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString(),
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value))
      });

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions?${params}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSubmissions(response.data.submissions);
      setTotalCount(response.data.total);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      showSnackbar('Failed to fetch submissions', 'error');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters, showSnackbar]);

  const fetchForms = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/forms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('Forms API response in SubmissionsList:', response.data); // Debug log
      
      // The backend now returns { forms: [...], totalPages, currentPage, total }
      const formsData = response.data.forms || response.data || [];
      
      // Ensure formsData is an array
      if (Array.isArray(formsData)) {
        setForms(formsData);
      } else {
        console.error('Forms data is not an array in SubmissionsList:', formsData);
        setForms([]);
      }
    } catch (error) {
      console.error('Error fetching forms:', error);
      setForms([]); // Ensure forms is always an array
    }
  }, []);

  useEffect(() => {
    fetchSubmissions();
  }, [fetchSubmissions]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  const handleDeleteSubmission = async (submissionId: string) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/submissions/${submissionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubmissions(prev => prev.filter(s => s.id !== submissionId));
      showSnackbar('Submission deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting submission:', error);
      showSnackbar('Failed to delete submission', 'error');
    }
  };

  const openEditDialog = async (submission: Submission) => {
    setEditDialog({ open: true, submission, formData: {}, loading: true, saving: false });
    
    try {
      // Fetch full submission details including form fields
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/submissions/${submission.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Initialize form data with current submission data
      const fullSubmission = response.data;
      setEditDialog({ 
        open: true, 
        submission: fullSubmission, 
        formData: { ...fullSubmission.data }, 
        loading: false, 
        saving: false 
      });
    } catch (error) {
      console.error('Error fetching submission details for edit:', error);
      setEditDialog({ 
        open: true, 
        submission, 
        formData: { ...submission.data }, 
        loading: false, 
        saving: false 
      });
      showSnackbar('Could not load full submission details', 'warning');
    }
  };

  const handleSaveSubmission = async () => {
    if (!editDialog.submission) return;
    
    setEditDialog(prev => ({ ...prev, saving: true }));
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/submissions/${editDialog.submission.id}`,
        { data: editDialog.formData },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the submission in the list
      setSubmissions(prev => prev.map(s => 
        s.id === editDialog.submission!.id 
          ? { ...s, data: editDialog.formData }
          : s
      ));
      
      setEditDialog({ open: false, submission: null, formData: {}, loading: false, saving: false });
      showSnackbar('Submission updated successfully', 'success');
    } catch (error) {
      console.error('Error updating submission:', error);
      showSnackbar('Failed to update submission', 'error');
      setEditDialog(prev => ({ ...prev, saving: false }));
    }
  };

  const handleFormFieldChange = (fieldName: string, value: any) => {
    setEditDialog(prev => ({
      ...prev,
      formData: {
        ...prev.formData,
        [fieldName]: value
      }
    }));
  };

  const [exporting, setExporting] = useState<string | null>(null);
  const [exportTest, setExportTest] = useState<any>(null);

  const testExport = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/export/test`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setExportTest(response.data);
      showSnackbar('Export test completed', 'success');
    } catch (error) {
      console.error('Export test failed:', error);
      showSnackbar('Export test failed', 'error');
    }
  };

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      setExporting(format);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        format,
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value))
      });

      showSnackbar(`Preparing ${format.toUpperCase()} export...`, 'info');

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/export/submissions?${params}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
          timeout: 120000, // 2 minutes timeout for large exports
        }
      );

      // Check if response is actually a blob or an error JSON
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.message || 'Export failed');
      }

      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = `submissions_export_${new Date().toISOString().split('T')[0]}.${format}`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      // Calculate approximate record count for user feedback
      const recordCount = submissions.length;
      const message = `${format.toUpperCase()} export completed successfully! Downloaded ${filename} with ${recordCount} record(s) in BDT currency.`;
      showSnackbar(message, 'success');
    } catch (error: any) {
      console.error('Error exporting submissions:', error);
      
      let errorMessage = 'Failed to export submissions';
      if (error.response?.status === 404) {
        errorMessage = 'No submissions found for the specified criteria';
      } else if (error.response?.status === 400) {
        errorMessage = error.response.data?.message || 'Invalid export parameters';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showSnackbar(errorMessage, 'error');
    } finally {
      setExporting(null);
    }
  };

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    setPage(0);

    // Update URL params
    const newSearchParams = new URLSearchParams();
    Object.entries(newFilters).forEach(([k, v]) => {
      if (v) newSearchParams.set(k, v);
    });
    setSearchParams(newSearchParams);
  };

  const getPaymentStatusColor = (status: string) => {
    switch (status) {
      case 'completed': return 'success';
      case 'pending': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const renderSubmissionData = (data: Record<string, any>, form?: { fields?: any[] }) => {
    console.log('Rendering submission data:', { data, form, hasFields: !!form?.fields });
    
    if (!data || Object.keys(data).length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No data available for this submission
        </Typography>
      );
    }

    if (!form?.fields || !Array.isArray(form.fields) || form.fields.length === 0) {
      console.log('Using fallback rendering - no form fields available');
      // Fallback to original rendering if no form fields available
      return Object.entries(data).map(([key, value]) => (
        <Box key={key} mb={1}>
          <Typography variant="body2" fontWeight="bold">
            {key}:
          </Typography>
          <Typography variant="body2">
            {Array.isArray(value) ? value.join(', ') : String(value)}
          </Typography>
        </Box>
      ));
    }

    // Filter submission data based on field visibility
    const filteredData = filterSubmissionData(data, form.fields, user);
    const visibleFieldIds = new Set(Object.keys(filteredData));

    console.log('Using form fields rendering with', form.fields.length, 'fields');
    console.log('Available data keys:', Object.keys(data));
    console.log('Filtered data keys:', Object.keys(filteredData));
    console.log('Form field IDs:', form.fields.map(f => f.id));
    
    // Use form fields as the primary structure, but only show visible ones
    const renderedFields = form.fields
      .filter((field) => field.type !== 'html') // Hide HTML content fields from submission list view
      .map((field) => {
        // Try multiple ways to match the field data
        let value = filteredData[field.id]; // Direct ID match
        let matchedKey = field.id;
      
      if (value === undefined || value === null || value === '') {
        value = filteredData[`field_${field.id}`]; // Try with field_ prefix
        matchedKey = `field_${field.id}`;
      }
      if (value === undefined || value === null || value === '') {
        value = filteredData[field.name]; // Try field name
        matchedKey = field.name;
      }
      
      console.log(`Field ${field.id} (${field.label}): looking for [${field.id}, field_${field.id}, ${field.name}] -> found "${matchedKey}" = "${value}"`);
      
      // Skip if field is not visible to current user
      if (!visibleFieldIds.has(field.id) && !visibleFieldIds.has(`field_${field.id}`) && !visibleFieldIds.has(field.name)) {
        return null;
      }
      
      if (value === undefined || value === null || value === '') return null;

      const visibilityInfo = getFieldVisibilityInfo(field);
      const isRestricted = field.visibility?.level && field.visibility.level !== 'public';

      return (
        <Box key={`field-${field.id}`} mb={1}>
          <Box display="flex" alignItems="center" gap={1} mb={0.5}>
            <Typography variant="body2" fontWeight="bold">
              {field.label || field.name || field.id}:
            </Typography>
            {isRestricted && (
              <Chip
                label={visibilityInfo.level}
                size="small"
                color={visibilityInfo.color}
                sx={{ 
                  fontSize: '0.6rem',
                  height: '16px',
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
          </Box>
          <Typography variant="body2">
            {Array.isArray(value) ? value.join(', ') : String(value)}
          </Typography>
        </Box>
      );
    }).filter(Boolean); // Remove null entries

    // Show admin notice if there are hidden fields
    const hiddenFieldsCount = form.fields.length - Object.keys(filteredData).length;
    const adminNotice = ['admin', 'super_admin'].includes(user?.role || '') && hiddenFieldsCount > 0 && (
      <Alert severity="info" sx={{ mb: 2 }}>
        Admin View: {hiddenFieldsCount} field(s) are hidden from public users but visible to you.
      </Alert>
    );

    // If no fields matched the data, show all visible data as fallback
    if (renderedFields.length === 0) {
      console.log('No matching fields found, using fallback');
      return (
        <>
          {adminNotice}
          {Object.entries(filteredData).map(([key, value]) => (
            <Box key={key} mb={1}>
              <Typography variant="body2" fontWeight="bold">
                {key}:
              </Typography>
              <Typography variant="body2">
                {Array.isArray(value) ? value.join(', ') : String(value)}
              </Typography>
            </Box>
          ))}
        </>
      );
    }

    return (
      <>
        {adminNotice}
        {renderedFields}
      </>
    );
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Submissions</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={exporting === 'csv' ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => handleExport('csv')}
            disabled={exporting !== null}
          >
            {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="outlined"
            startIcon={exporting === 'pdf' ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={() => handleExport('pdf')}
            disabled={exporting !== null}
          >
            {exporting === 'pdf' ? 'Exporting...' : 'Export PDF'}
          </Button>
          {process.env.NODE_ENV === 'development' && (
            <Button
              variant="text"
              size="small"
              onClick={testExport}
            >
              Test Export
            </Button>
          )}
        </Box>
      </Box>

      {/* Export Test Results (Development only) */}
      {process.env.NODE_ENV === 'development' && exportTest && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Export Test Results:</strong><br />
            Submissions: {exportTest.submissionCount}<br />
            Directory: {exportTest.directoryExists ? '✓' : '✗'}<br />
            Available: {exportTest.availableFormats?.join(', ')}<br />
            Test URLs: <br />
            • CSV: {window.location.origin.replace(':3000', ':5000')}{exportTest.testEndpoints?.csv}<br />
            • PDF: {window.location.origin.replace(':3000', ':5000')}{exportTest.testEndpoints?.pdf}
          </Typography>
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <FilterIcon />
          <Typography variant="h6">Filters</Typography>
          
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Form</InputLabel>
            <Select
              value={filters.formId}
              onChange={(e) => handleFilterChange('formId', e.target.value)}
            >
              <MenuItem value="">All Forms</MenuItem>
              {forms.map((form) => (
                <MenuItem key={form.id} value={form.id}>
                  {form.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Payment Status</InputLabel>
            <Select
              value={filters.paymentStatus}
              onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="none">No Payment</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="completed">Completed</MenuItem>
              <MenuItem value="failed">Failed</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="From Date"
            type="date"
            size="small"
            value={filters.dateFrom}
            onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="To Date"
            type="date"
            size="small"
            value={filters.dateTo}
            onChange={(e) => handleFilterChange('dateTo', e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Button
            variant="outlined"
            onClick={() => {
              setFilters({ formId: '', paymentStatus: '', dateFrom: '', dateTo: '' });
              setSearchParams({});
              setPage(0);
            }}
          >
            Clear Filters
          </Button>
        </Box>
      </Paper>

      {/* Submissions Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Form</TableCell>
              <TableCell>Unique ID</TableCell>
              <TableCell>Edit Code</TableCell>
              <TableCell>Submitted At</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell>Amount</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : submissions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body2" color="textSecondary">
                    No submissions found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              // Group submissions by form
              Object.entries(
                submissions.reduce((acc, submission) => {
                  const formKey = submission.form?.title || 'Unknown Form';
                  if (!acc[formKey]) {
                    acc[formKey] = [];
                  }
                  acc[formKey].push(submission);
                  return acc;
                }, {} as Record<string, Submission[]>)
              ).map(([formTitle, formSubmissions]) => 
                formSubmissions.map((submission, index) => (
                  <TableRow key={`${formTitle}-${submission.id}`}>
                    <TableCell>
                      {index === 0 ? formTitle : ''}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {submission.uniqueId || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">
                        {submission.editCode || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {new Date(submission.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={submission.paymentInfo?.status || 'none'}
                        color={getPaymentStatusColor(submission.paymentInfo?.status || 'none') as any}
                        size="small"
                        icon={submission.paymentInfo?.status ? <PaymentIcon /> : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      {submission.paymentInfo?.amount ? `৳${submission.paymentInfo.amount}` : '-'}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => openSubmissionDialog(submission)}
                        title="View Submission"
                      >
                        <ViewIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => openEditDialog(submission)}
                        title="Edit Submission"
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteSubmission(submission.id)}
                        title="Delete Submission"
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ).flat()
            )}
          </TableBody>
        </Table>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>

      {/* View Submission Dialog */}
      <Dialog
        open={viewDialog.open}
        onClose={() => setViewDialog({ open: false, submission: null, loading: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Submission Details
          {viewDialog.submission && (
            <Typography variant="subtitle2" color="textSecondary">
              Form: {viewDialog.submission.form?.title || 'Unknown Form'}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent>
          {viewDialog.loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading submission details...</Typography>
            </Box>
          ) : viewDialog.submission && (
            <Box>
              {/* Debug info in development */}
              {process.env.NODE_ENV === 'development' && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Debug Info:</strong><br />
                    Form Fields: {viewDialog.submission.form?.fields?.length || 0}<br />
                    Data Keys: {Object.keys(viewDialog.submission.data || {}).length}<br />
                    Data: {JSON.stringify(Object.keys(viewDialog.submission.data || {})).slice(0, 100)}...
                  </Typography>
                </Alert>
              )}

              <Alert severity="info" sx={{ mb: 2 }}>
                Submitted on: {new Date(viewDialog.submission.createdAt).toLocaleString()}
              </Alert>

              {viewDialog.submission.paymentInfo?.status && viewDialog.submission.paymentInfo.status !== 'none' && (
                <Alert 
                  severity={
                    viewDialog.submission.paymentInfo.status === 'completed' ? 'success' : 
                    viewDialog.submission.paymentInfo.status === 'pending' ? 'warning' : 'error'
                  }
                  sx={{ mb: 2 }}
                >
                  Payment Status: {viewDialog.submission.paymentInfo.status}
                  {viewDialog.submission.paymentInfo.amount && 
                    ` - Amount: ৳${viewDialog.submission.paymentInfo.amount}`
                  }
                </Alert>
              )}

              <Typography variant="h6" gutterBottom>
                Form Data
              </Typography>
              
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {renderSubmissionData(viewDialog.submission.data, viewDialog.submission.form)}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setViewDialog({ open: false, submission: null, loading: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Submission Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, submission: null, formData: {}, loading: false, saving: false })}
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          Edit Submission
          {editDialog.submission && (
            <Typography variant="subtitle2" color="textSecondary">
              Form: {editDialog.submission.form.title}
            </Typography>
          )}
        </DialogTitle>
        
        <DialogContent>
          {editDialog.loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
            </Box>
          ) : editDialog.submission && (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are editing a submitted form. Changes will be saved immediately and cannot be undone.
              </Alert>

              <Typography variant="body2" color="textSecondary" gutterBottom>
                Submitted on: {new Date(editDialog.submission.createdAt).toLocaleString()}
              </Typography>

              {editDialog.submission.paymentInfo?.status && editDialog.submission.paymentInfo.status !== 'none' && (
                <Alert 
                  severity={
                    editDialog.submission.paymentInfo.status === 'completed' ? 'success' : 
                    editDialog.submission.paymentInfo.status === 'pending' ? 'warning' : 'error'
                  }
                  sx={{ mb: 2 }}
                >
                  Payment Status: {editDialog.submission.paymentInfo.status}
                  {editDialog.submission.paymentInfo.amount && 
                    ` - Amount: ৳${editDialog.submission.paymentInfo.amount}`
                  }
                </Alert>
              )}

              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                Form Fields
              </Typography>
              
              <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                {editDialog.submission.form?.fields?.map((field: any) => {
                  // Get the field key (handle different naming patterns)
                  const fieldKey = editDialog.submission!.data[field.id] !== undefined 
                    ? field.id 
                    : editDialog.submission!.data[`field_${field.id}`] !== undefined 
                    ? `field_${field.id}`
                    : field.name;

                  const currentValue = editDialog.formData[fieldKey] || '';

                  return (
                    <Box key={field.id} sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        {field.label}
                        {field.type && (
                          <Typography component="span" variant="caption" color="textSecondary">
                            {' '}({field.type})
                          </Typography>
                        )}
                      </Typography>
                      
                      {field.type === 'textarea' ? (
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          value={currentValue}
                          onChange={(e) => handleFormFieldChange(fieldKey, e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      ) : field.type === 'select' || field.type === 'radio' ? (
                        <FormControl fullWidth size="small">
                          <Select
                            value={currentValue}
                            onChange={(e) => handleFormFieldChange(fieldKey, e.target.value)}
                          >
                            {field.options?.map((option: string) => (
                              <MenuItem key={option} value={option}>
                                {option}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : field.type === 'checkbox' ? (
                        <Box>
                          {field.options?.map((option: string) => (
                            <Box key={option}>
                              <label>
                                <input
                                  type="checkbox"
                                  checked={Array.isArray(currentValue) ? currentValue.includes(option) : currentValue === option}
                                  onChange={(e) => {
                                    if (Array.isArray(currentValue)) {
                                      const newValue = e.target.checked
                                        ? [...currentValue, option]
                                        : currentValue.filter(v => v !== option);
                                      handleFormFieldChange(fieldKey, newValue);
                                    } else {
                                      handleFormFieldChange(fieldKey, e.target.checked ? option : '');
                                    }
                                  }}
                                  style={{ marginRight: '8px' }}
                                />
                                {option}
                              </label>
                            </Box>
                          ))}
                        </Box>
                      ) : field.type === 'tel' ? (
                        <PhoneInput
                          value={currentValue}
                          onChange={(number, fullNumber) => handleFormFieldChange(fieldKey, fullNumber)}
                          fullWidth
                          margin="none"
                        />
                      ) : (
                        <TextField
                          fullWidth
                          type={field.type === 'number' ? 'number' : field.type === 'email' ? 'email' : 'text'}
                          value={currentValue}
                          onChange={(e) => handleFormFieldChange(fieldKey, e.target.value)}
                          variant="outlined"
                          size="small"
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button 
            onClick={() => setEditDialog({ open: false, submission: null, formData: {}, loading: false, saving: false })}
            disabled={editDialog.saving}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveSubmission}
            variant="contained"
            disabled={editDialog.saving}
          >
            {editDialog.saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubmissionsList;
